import React, { useEffect, useState } from "react";
import { useIsFocused, useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";

import { View, StyleSheet } from "react-native";
import {
  Img,
  Text,
  Input,
  Error,
  Toast,
  Switch,
  Header,
  Button,
  Loading,
  CheckBox,
  InputLabel,
  ConfirmAlert,
  ParentCatPicker,
} from "components";
import DetailsTemplate from "screens/components/DetailsTemplate";
import Variations from "./Variations";
import Pictures from "./pictures";

import { useForm, Controller } from "react-hook-form";

import {
  useProduct,
  useAddProduct,
  useEditProduct,
  useDeleteProduct,
  useCategoryAttributes,
  useVariantProductAttributes,
} from "clients/apis";

import { colors, sizes, strings } from "assets/constants";
import { mq, changeArrayAt, sn, alertPermission } from "utils";
import { useRecoilValue } from "recoil";
import { userAtom } from "stores";
import { useChmod, useFlag } from "hooks";
import css from "styles";
import { _goToBack } from "routers/helper";
import useInitialLoading from "hooks/useInitialLoading";

interface FormDataT {
  title?: string;
  category?: { id?: number; title?: string };
  price: string | number;
  checkbox: 0 | 1;
  isActive: 0 | 1;
  recommended: 0 | 1;
  discount: 0 | 1;
  attrs: { id?: number; title?: string }[];
  variations: Array<ProductVariationT>;
  images: Array<ImageUploaderT>;
}

export default function Product() {
  const { chmod } = useChmod();

  const { initialLoading } = useInitialLoading();
  const isFocused = useIsFocused();
  const user = useRecoilValue(userAtom);
  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();
  const {
    flg: uploadPics,
    on: showUploadPics,
    off: hideUploadPics,
  } = useFlag(false);
  const _onHideUploadPics = () => {
    if (id) {
      refetch();
      hideUploadPics();
    } else {
      hideUploadPics();
      _goToBack();
    }
  };

  const { id, catId } = useRoute<RootRouteProps<"product">>().params;
  const headerTitle = id ? strings?.edit_product : strings?.add_product;

  const [submittedProductId, setSubmittedProductId] = useState<number>();

  const { data: variantAttrs } = useVariantProductAttributes({
    productId: id,
    categoryId: catId,
  });

  const { isLoading, data, error, isError, refetch, isRefetching } =
    useProduct(id);

  const {
    watch,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormDataT>({
    defaultValues: {
      title: "",
      category: undefined,
      price: 0,
      checkbox: 0,
      attrs: [],
      variations: [],
      isActive: 1,
      recommended: 0,
      discount: 0,
    },
  });

  useEffect(() => {
    if (data) {
      setValue("title", data.data?.title);
      setValue("category", data.data?.category);
      setValue("price", Number(data.data?.price) > 0 ? data.data?.price : 0);
      setValue("variations", data.data?.variations);
      setValue("isActive", data.data?.is_active);
      setValue("recommended", data.data?.recommended);
      setValue("discount", data.data?.discount);
      setValue("images", data.data?.images || []);
    }
  }, [data, setValue]);

  useEffect(() => {
    id && isFocused && refetch();
  }, [id, isFocused, refetch]);

  const addProductMut = useAddProduct();
  const editProductMut = useEditProduct();
  const deleteProductMut = useDeleteProduct();

  const _onSubmit = () => {
    const {
      title,
      category,
      price,
      checkbox,
      attrs,
      isActive,
      recommended,
      discount,
    } = getValues();

    const postData: ProductReqT = {
      title,
      category_id: category?.id,
      unit_price: Number(price) > 0 ? Number(price) : 0,
      is_active: isActive,
      recommended,
      discount,
    };
    checkbox > 0 &&
      attrs.length > 0 &&
      Object.assign(postData, {
        attributes: attrs.map((attr: { id?: number }) => attr.id),
      });

    addProductMut.mutate(postData, {
      onSuccess: (res) => {
        setSubmittedProductId(res?.data?.product_id);
        showUploadPics();
      },
    });
  };

  const makeEditPostData = (updatePrice = false) => {
    const {
      title,
      category,
      price,
      variations,
      isActive,
      recommended,
      discount,
    } = getValues();

    const vars = variations.map((v) => ({
      id: v.id,
      price: Number(v.price),
      is_active: v.is_active,
      is_binary: v.is_binary,
      attributes: v.attributes
        ? v.attributes?.map((attr) => attr.id)
        : undefined,
    }));

    const postData: ProductReqT = {
      title: title,
      category_id: category?.id,
      unit_price: Number(price) > 0 ? Number(price) : 0,
      update_prices: updatePrice,
      variations: vars,
      is_active: isActive,
      recommended,
      discount,
    };

    return postData;
  };

  const _onEditWithUpdatePrice = () =>
    editProductMut.mutate({ id: data?.data?.id, data: makeEditPostData(true) });

  const _onEditWithoutUpdatePrice = () =>
    editProductMut.mutate({
      id: data?.data?.id,
      data: makeEditPostData(false),
    });

  const _onEdit =
    +data?.data.price !== +getValues().price
      ? showAlert
      : _onEditWithoutUpdatePrice;

  const _onDeleteConfirm = (close: () => void) =>
    deleteProductMut.mutate(data?.data.id, { onSuccess: close });

  const selectCatWarning = () =>
    Toast.warning({ message: strings.can_not_edit_product_cat });

  const checkedBoxWarn = () =>
    Toast.warning({ message: strings.pick_product_cat_for_first });

  const { data: catAttrs, isFetching: catAttrLoading } = useCategoryAttributes(
    watch().category?.id
  );

  const _onChangePrice = (newPrice: number | string) => {
    const varsField = getValues().variations;
    const editedVars: Array<
      { index: number; value: ProductVariationT } | false
    > = varsField
      .map((v, i) =>
        v.id === null && v.total_price_ratio
          ? {
              index: i,
              value: { ...v, price: +v.total_price_ratio * +newPrice },
            }
          : false
      )
      .filter(Boolean);

    editedVars.map((v: { index: number; value: ProductVariationT } | false) => {
      if (v) {
        varsField[v.index] = v.value;
      }
    });

    setValue("variations", varsField);
  };

  const _renderContent = () => {
    if (id && (isLoading || initialLoading)) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <DetailsTemplate
        onDelete={_onDeleteConfirm}
        deletable={data && chmod("product", "DELETE") ? true : false}
        deleteLoading={deleteProductMut?.isLoading}
        deleteDescription={strings.delete_product_description}
        submitLabel={!data ? strings.submit_info : strings.edit_info}
        submitLoading={addProductMut?.isLoading || editProductMut?.isLoading}
        onSubmit={
          data
            ? chmod("product", "PUT")
              ? handleSubmit(_onEdit)
              : alertPermission
            : chmod("product", "POST")
            ? handleSubmit(_onSubmit)
            : alertPermission
        }
      >
        {id && (
          <View style={{ marginBottom: mq(15) }}>
            <InputLabel label={strings.pictures} />

            <View
              style={{
                ...css.wrap,
                ...css.rRowCenterAlign,
              }}
            >
              {data?.data?.images?.map(
                (image: { id: number; url: string }, key: number) => {
                  return (
                    <View key={String(key)} style={styles.imageContainer}>
                      <Img
                        source={{ uri: image?.url }}
                        style={{
                          ...css.fitHeight,
                          ...css.fitWidth,
                          borderRadius: sizes.radius.md,
                          backgroundColor: colors.progressBarInactive,
                        }}
                      />
                    </View>
                  );
                }
              )}
              <View style={styles.imageContainer}>
                <Button
                  onPress={showUploadPics}
                  square={"90%"}
                  icon={{
                    name:
                      data?.data?.images?.length > 0
                        ? "more-horizontal"
                        : "plus",
                    size: 35,
                    color: user.color,
                  }}
                  bgColor={colors.white}
                />
              </View>
            </View>
          </View>
        )}

        <InputLabel label={strings.title} />
        <Controller
          control={control}
          name="title"
          rules={{
            required: { value: true, message: strings.fill_input },
          }}
          render={({ field: { onChange, value } }) => {
            return (
              <Input
                value={value}
                onChangeText={onChange}
                placeholder={strings.insert_title}
                icon={{ name: "edit-3" }}
                error={errors.title?.message as string}
              />
            );
          }}
        />

        <InputLabel label={strings.product_cat} />
        <Controller
          control={control}
          name="category"
          rules={{
            required: { value: true, message: strings.pick_input },
          }}
          render={({ field: { onChange, value } }) => {
            return (
              <ParentCatPicker
                showChild
                hideParents
                onPress={data ? selectCatWarning : undefined}
                value={value}
                placeholder={strings.pick_product_cat}
                onSelect={onChange}
                error={errors.category?.message as string}
              />
            );
          }}
        />

        {!data && catAttrs?.data?.length > 0 && (
          <>
            <InputLabel label={strings.add_attrs_to_title} />
            <Controller
              control={control}
              name="checkbox"
              render={({ field: { value, onChange } }) => {
                return (
                  <CheckBox
                    title={strings.create_group_products}
                    value={value}
                    onChange={onChange}
                    onPress={!watch().category ? checkedBoxWarn : undefined}
                  />
                );
              }}
            />
            <Text style={{ lineHeight: 21 }} color={colors.textColorGray}>
              {strings.create_group_products_description}
            </Text>
            {watch().checkbox > 0 &&
              (catAttrLoading ? (
                <View style={styles.loadingContainer}>
                  <Loading size={15} />
                </View>
              ) : catAttrs?.data?.length === 0 ? (
                <View style={styles.emptyAttrContainer}>
                  <Text>{strings.no_attr_cat}</Text>
                </View>
              ) : (
                <>
                  <Text style={{ top: mq(-5) }}>{strings.pick_attr_items}</Text>
                  <View style={styles.attrItemsSection}>
                    {catAttrs?.data?.map(
                      (item: AttributeItemT, key: number) => {
                        const itemIndex = Number(
                          watch().attrs?.findIndex((e) => e.id === item.id)
                        );
                        const isSelected = itemIndex > -1;
                        return (
                          <Button
                            key={String(key)}
                            onPress={() => {
                              let tmp = watch().attrs ?? [];
                              if (isSelected) {
                                tmp = changeArrayAt(tmp, itemIndex);
                              } else {
                                tmp?.push(item);
                              }
                              setValue("attrs", tmp);
                            }}
                            width={"auto"}
                            height={mq(24)}
                            title={{ value: item.title, size: "sm" }}
                            contentSeparator={4}
                            bgColor={
                              isSelected
                                ? user?.color || colors.theme
                                : colors.white
                            }
                            style={{
                              paddingHorizontal: mq(15),
                              marginLeft: mq(7),
                              marginBottom: mq(7),
                              opacity: isSelected ? 1 : 0.8,
                              borderWidth: 1,
                              borderColor: colors.textColorGray,
                            }}
                          />
                        );
                      }
                    )}
                  </View>
                </>
              ))}
          </>
        )}

        <InputLabel label={strings.unit_price} />
        <Controller
          control={control}
          name="price"
          render={({ field: { onChange, value } }) => (
            <Input
              value={String(value)}
              onChangeText={(val) => {
                console.log(val);
                onChange(val);
                _onChangePrice(val);
              }}
              placeholder={strings.insert_unit_price}
              icon={{ name: "tag" }}
              keyboardType="number-pad"
              enablePriceFormatter
            />
          )}
        />

        <View style={{ ...css.rRowCenterAlign }}>
          <View style={{ width: mq(5) }} />
          <Controller
            control={control}
            name="isActive"
            render={({ field: { onChange, value } }) => (
              <View style={{ marginBottom: sizes.space * 2 }}>
                <Switch
                  value={value}
                  onChange={onChange}
                  activeLabel={strings.active}
                  inactiveLabel={strings.active}
                />
              </View>
            )}
          />
          <View style={{ width: mq(20) }} />

          <Controller
            control={control}
            name="recommended"
            render={({ field: { onChange, value } }) => (
              <View style={{ marginBottom: sizes.space * 2 }}>
                <Switch
                  value={value}
                  onChange={onChange}
                  activeLabel={strings.recommended}
                  inactiveLabel={strings.recommended}
                />
              </View>
            )}
          />

          <View style={{ width: mq(20) }} />

          <Controller
            control={control}
            name="discount"
            render={({ field: { onChange, value } }) => (
              <View style={{ marginBottom: sizes.space * 2 }}>
                <Switch
                  value={value}
                  onChange={onChange}
                  activeLabel={strings.discounted}
                  inactiveLabel={strings.discounted}
                />
              </View>
            )}
          />
        </View>

        {data && data.data.type === "variable" && (
          <>
            <InputLabel label={strings.variants} />
            <Controller
              control={control}
              name="variations"
              render={({ field: { onChange, value } }) => (
                <Variations
                  items={value}
                  onChange={onChange}
                  unitPrice={getValues().price}
                  variantAttrs={variantAttrs?.data?.attributes}
                />
              )}
            />
          </>
        )}

        <ConfirmAlert
          force
          show={alert}
          hide={hideAlert}
          renderContent={(close) => (
            <>
              <Text size={"lg"} weight="bold" align="center">
                {strings.will_the_prices_be_updated}
              </Text>
              <Text
                style={{
                  marginTop: 10,
                  lineHeight: 22,
                  marginBottom: 15,
                }}
              >
                {strings.update_price_descriptions}
              </Text>
              <View
                style={{ ...css.rRowCenterAlign, ...css.justifySpaceEvenly }}
              >
                <Button
                  title={{ value: strings.confirm }}
                  width={"40%"}
                  onPress={() => {
                    _onEditWithUpdatePrice();
                    close();
                  }}
                />
                <Button
                  outline
                  width={"40%"}
                  title={{ value: strings.no_update, size: "md" }}
                  onPress={() => {
                    _onEditWithoutUpdatePrice();
                    close();
                  }}
                />
              </View>
            </>
          )}
        />
      </DetailsTemplate>
    );
  };

  return (
    <>
      <Header title={headerTitle} />
      {_renderContent()}

      <Pictures
        visible={uploadPics}
        hide={_onHideUploadPics}
        productId={submittedProductId || data?.data?.id}
        images={data?.data?.images}
      />
    </>
  );
}

const styles = StyleSheet.create({
  attrItemsSection: {
    top: mq(-5),
    marginBottom: mq(10),
    flexWrap: "wrap",
    alignItems: "center",
    flexDirection: "row-reverse",
  },

  emptyAttrContainer: {
    width: "100%",
    borderWidth: 1,
    borderStyle: "dashed",
    top: mq(-10),
    padding: mq(12),
    marginBottom: mq(17),
    borderRadius: sizes.radius.lg,
  },

  loadingContainer: {
    minHeight: mq(65),
    paddingRight: mq(12),
    alignSelf: "flex-end",
  },

  imageContainer: {
    width: sn("20%", "16.64%", "12.5%", "12.5%"),
    aspectRatio: 0.75,
    padding: mq(3),
    marginBottom: mq(1),
    alignItems: "center",
    justifyContent: "center",
  },
});
